package org.example.project.ui.screens

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.example.project.ui.components.StatusBadge
import org.example.project.ui.icons.AppIcons
import org.example.project.ui.theme.EditorColors
import org.example.project.ui.viewmodel.ActivityFormUiState

@Composable
fun ActivityFormScreen(
    state: ActivityFormUiState,
    onNameChange: (String) -> Unit,
    onDescriptionChange: (String) -> Unit,
    onCategoryChange: (String) -> Unit,
    onMinAgeChange: (String) -> Unit,
    onMaxAgeChange: (String) -> Unit,
    onDurationChange: (String) -> Unit,
    onVideoUrlChange: (String) -> Unit,
    onCancel: () -> Unit,
    onSubmit: () -> Unit,
    modifier: Modifier = Modifier
) {
    val isEdit = state.isEdit
    val validationErrors = state.validationErrors
    val hasSubmittedOnce = state.hasSubmittedOnce

    Column(
        modifier = modifier
            .fillMaxWidth()
            .widthIn(max = 840.dp)
    ) {
        // Top Back link with ArrowBack icon
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .clickable { onCancel() }
                .padding(vertical = 12.dp)
        ) {
            Icon(
                imageVector = AppIcons.ArrowBack,
                contentDescription = null,
                tint = EditorColors.Primary,
                modifier = Modifier.size(16.dp)
            )
            Spacer(modifier = Modifier.width(6.dp))
            Text(
                text = "Powrót do katalogu",
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
                color = EditorColors.Primary
            )
        }

        Spacer(modifier = Modifier.height(4.dp))

        // Main Form Card
        Card(
            modifier = Modifier.fillMaxWidth(),
            colors = CardDefaults.cardColors(containerColor = EditorColors.Surface),
            shape = RoundedCornerShape(16.dp),
            border = BorderStroke(1.dp, EditorColors.BorderSubtle),
            elevation = CardDefaults.cardElevation(defaultElevation = 1.dp)
        ) {
            Column(
                modifier = Modifier
                    .padding(28.dp)
                    .verticalScroll(rememberScrollState())
            ) {
                // Header with title and status badge
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Column {
                        Text(
                            text = if (isEdit) "Edycja zabawy" else "Nowy szkic zabawy",
                            fontSize = 20.sp,
                            fontWeight = FontWeight.Bold,
                            color = EditorColors.TextPrimary
                        )
                        Spacer(modifier = Modifier.height(2.dp))
                        Text(
                            text = if (isEdit) "Zaktualizuj parametry i treść zabawy" else "Wypełnij formularz, aby utworzyć nowy szkic zabawy w katalogu",
                            fontSize = 13.sp,
                            color = EditorColors.TextSecondary
                        )
                    }

                    if (state.activityToEdit != null) {
                        StatusBadge(status = state.activityToEdit.status)
                    }
                }

                Spacer(modifier = Modifier.height(24.dp))

                // Section 1: Podstawowe informacje
                FormSectionTitle(title = "Podstawowe informacje")

                Spacer(modifier = Modifier.height(12.dp))

                // Nazwa
                OutlinedTextField(
                    value = state.name,
                    onValueChange = onNameChange,
                    label = { Text("Nazwa zabawy (1–200 znaków)") },
                    placeholder = { Text("np. Berek zamrażaniec") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    isError = hasSubmittedOnce && validationErrors.nameError != null,
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = EditorColors.Primary,
                        unfocusedBorderColor = EditorColors.Border
                    )
                )
                if (hasSubmittedOnce && validationErrors.nameError != null) {
                    Text(
                        text = validationErrors.nameError!!,
                        color = EditorColors.ErrorSolid,
                        fontSize = 12.sp,
                        modifier = Modifier.padding(start = 4.dp, top = 4.dp)
                    )
                }

                Spacer(modifier = Modifier.height(16.dp))

                // Kategoria / Rodzaj
                OutlinedTextField(
                    value = state.category,
                    onValueChange = onCategoryChange,
                    label = { Text("Rodzaj / Kategoria (1–100 znaków)") },
                    placeholder = { Text("np. Ruchowa, Plastyczna, Integracyjna, Spokojna") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    isError = hasSubmittedOnce && validationErrors.categoryError != null,
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = EditorColors.Primary,
                        unfocusedBorderColor = EditorColors.Border
                    )
                )
                if (hasSubmittedOnce && validationErrors.categoryError != null) {
                    Text(
                        text = validationErrors.categoryError!!,
                        color = EditorColors.ErrorSolid,
                        fontSize = 12.sp,
                        modifier = Modifier.padding(start = 4.dp, top = 4.dp)
                    )
                }

                Spacer(modifier = Modifier.height(24.dp))

                // Section 2: Wymagania wiekowe i czas trwania
                FormSectionTitle(title = "Wymagania wiekowe i czas trwania")

                Spacer(modifier = Modifier.height(12.dp))

                BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
                    val isNarrow = maxWidth < 600.dp
                    if (isNarrow) {
                        Column(
                            modifier = Modifier.fillMaxWidth(),
                            verticalArrangement = Arrangement.spacedBy(12.dp)
                        ) {
                            Column {
                                OutlinedTextField(
                                    value = state.minAgeText,
                                    onValueChange = onMinAgeChange,
                                    label = { Text("Min wiek (0–120)") },
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    isError = hasSubmittedOnce && validationErrors.minAgeError != null,
                                    shape = RoundedCornerShape(10.dp),
                                    colors = OutlinedTextFieldDefaults.colors(
                                        focusedBorderColor = EditorColors.Primary,
                                        unfocusedBorderColor = EditorColors.Border
                                    )
                                )
                                if (hasSubmittedOnce && validationErrors.minAgeError != null) {
                                    Text(
                                        text = validationErrors.minAgeError!!,
                                        color = EditorColors.ErrorSolid,
                                        fontSize = 11.sp,
                                        modifier = Modifier.padding(start = 4.dp, top = 4.dp)
                                    )
                                }
                            }

                            Column {
                                OutlinedTextField(
                                    value = state.maxAgeText,
                                    onValueChange = onMaxAgeChange,
                                    label = { Text("Max wiek (0–120)") },
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    isError = hasSubmittedOnce && validationErrors.maxAgeError != null,
                                    shape = RoundedCornerShape(10.dp),
                                    colors = OutlinedTextFieldDefaults.colors(
                                        focusedBorderColor = EditorColors.Primary,
                                        unfocusedBorderColor = EditorColors.Border
                                    )
                                )
                                if (hasSubmittedOnce && validationErrors.maxAgeError != null) {
                                    Text(
                                        text = validationErrors.maxAgeError!!,
                                        color = EditorColors.ErrorSolid,
                                        fontSize = 11.sp,
                                        modifier = Modifier.padding(start = 4.dp, top = 4.dp)
                                    )
                                }
                            }

                            Column {
                                OutlinedTextField(
                                    value = state.durationText,
                                    onValueChange = onDurationChange,
                                    label = { Text("Czas w min (1–1440)") },
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    isError = hasSubmittedOnce && validationErrors.durationError != null,
                                    shape = RoundedCornerShape(10.dp),
                                    colors = OutlinedTextFieldDefaults.colors(
                                        focusedBorderColor = EditorColors.Primary,
                                        unfocusedBorderColor = EditorColors.Border
                                    )
                                )
                                if (hasSubmittedOnce && validationErrors.durationError != null) {
                                    Text(
                                        text = validationErrors.durationError!!,
                                        color = EditorColors.ErrorSolid,
                                        fontSize = 11.sp,
                                        modifier = Modifier.padding(start = 4.dp, top = 4.dp)
                                    )
                                }
                            }
                        }
                    } else {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            Column(modifier = Modifier.weight(1f)) {
                                OutlinedTextField(
                                    value = state.minAgeText,
                                    onValueChange = onMinAgeChange,
                                    label = { Text("Min wiek (0–120)") },
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    isError = hasSubmittedOnce && validationErrors.minAgeError != null,
                                    shape = RoundedCornerShape(10.dp),
                                    colors = OutlinedTextFieldDefaults.colors(
                                        focusedBorderColor = EditorColors.Primary,
                                        unfocusedBorderColor = EditorColors.Border
                                    )
                                )
                                if (hasSubmittedOnce && validationErrors.minAgeError != null) {
                                    Text(
                                        text = validationErrors.minAgeError!!,
                                        color = EditorColors.ErrorSolid,
                                        fontSize = 11.sp,
                                        modifier = Modifier.padding(start = 4.dp, top = 4.dp)
                                    )
                                }
                            }

                            Column(modifier = Modifier.weight(1f)) {
                                OutlinedTextField(
                                    value = state.maxAgeText,
                                    onValueChange = onMaxAgeChange,
                                    label = { Text("Max wiek (0–120)") },
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    isError = hasSubmittedOnce && validationErrors.maxAgeError != null,
                                    shape = RoundedCornerShape(10.dp),
                                    colors = OutlinedTextFieldDefaults.colors(
                                        focusedBorderColor = EditorColors.Primary,
                                        unfocusedBorderColor = EditorColors.Border
                                    )
                                )
                                if (hasSubmittedOnce && validationErrors.maxAgeError != null) {
                                    Text(
                                        text = validationErrors.maxAgeError!!,
                                        color = EditorColors.ErrorSolid,
                                        fontSize = 11.sp,
                                        modifier = Modifier.padding(start = 4.dp, top = 4.dp)
                                    )
                                }
                            }

                            Column(modifier = Modifier.weight(1f)) {
                                OutlinedTextField(
                                    value = state.durationText,
                                    onValueChange = onDurationChange,
                                    label = { Text("Czas w min (1–1440)") },
                                    modifier = Modifier.fillMaxWidth(),
                                    singleLine = true,
                                    isError = hasSubmittedOnce && validationErrors.durationError != null,
                                    shape = RoundedCornerShape(10.dp),
                                    colors = OutlinedTextFieldDefaults.colors(
                                        focusedBorderColor = EditorColors.Primary,
                                        unfocusedBorderColor = EditorColors.Border
                                    )
                                )
                                if (hasSubmittedOnce && validationErrors.durationError != null) {
                                    Text(
                                        text = validationErrors.durationError!!,
                                        color = EditorColors.ErrorSolid,
                                        fontSize = 11.sp,
                                        modifier = Modifier.padding(start = 4.dp, top = 4.dp)
                                    )
                                }
                            }
                        }
                    }
                }

                Spacer(modifier = Modifier.height(24.dp))

                // Section 3: Treść zabawy
                FormSectionTitle(title = "Opis zabawy i zasady")

                Spacer(modifier = Modifier.height(12.dp))

                OutlinedTextField(
                    value = state.description,
                    onValueChange = onDescriptionChange,
                    label = { Text("Opis zabawy, zasady i przygotowanie (1–10 000 znaków)") },
                    placeholder = { Text("Opisz dokładnie przebieg zabawy, niezbędne rekwizyty i wskazówki dla animatora...") },
                    modifier = Modifier
                        .fillMaxWidth()
                        .height(180.dp),
                    isError = hasSubmittedOnce && validationErrors.descriptionError != null,
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = EditorColors.Primary,
                        unfocusedBorderColor = EditorColors.Border
                    )
                )
                if (hasSubmittedOnce && validationErrors.descriptionError != null) {
                    Text(
                        text = validationErrors.descriptionError!!,
                        color = EditorColors.ErrorSolid,
                        fontSize = 12.sp,
                        modifier = Modifier.padding(start = 4.dp, top = 4.dp)
                    )
                }

                Spacer(modifier = Modifier.height(24.dp))

                // Section 4: Materiały wideo (opcjonalne)
                FormSectionTitle(title = "Wideo instruktażowe (opcjonalne)")

                Spacer(modifier = Modifier.height(12.dp))

                OutlinedTextField(
                    value = state.videoUrlText,
                    onValueChange = onVideoUrlChange,
                    label = { Text("Bezpośredni URL wideo (HTTPS, np. https://example.com/video.mp4)") },
                    placeholder = { Text("https://...") },
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    isError = hasSubmittedOnce && validationErrors.videoUrlError != null,
                    shape = RoundedCornerShape(10.dp),
                    colors = OutlinedTextFieldDefaults.colors(
                        focusedBorderColor = EditorColors.Primary,
                        unfocusedBorderColor = EditorColors.Border
                    )
                )
                if (hasSubmittedOnce && validationErrors.videoUrlError != null) {
                    Text(
                        text = validationErrors.videoUrlError!!,
                        color = EditorColors.ErrorSolid,
                        fontSize = 12.sp,
                        modifier = Modifier.padding(start = 4.dp, top = 4.dp)
                    )
                }

                Spacer(modifier = Modifier.height(32.dp))

                // Action Buttons Footer
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.End,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    OutlinedButton(
                        onClick = onCancel,
                        enabled = !state.isSaving,
                        shape = RoundedCornerShape(10.dp),
                        border = BorderStroke(1.dp, EditorColors.BorderStrong),
                        modifier = Modifier.height(44.dp)
                    ) {
                        Text("Anuluj", fontSize = 13.sp, color = EditorColors.TextSecondary)
                    }

                    Spacer(modifier = Modifier.width(12.dp))

                    Button(
                        onClick = onSubmit,
                        enabled = !state.isSaving,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = EditorColors.Primary,
                            contentColor = EditorColors.Surface
                        ),
                        modifier = Modifier.height(44.dp)
                    ) {
                        if (state.isSaving) {
                            CircularProgressIndicator(
                                color = EditorColors.Surface,
                                modifier = Modifier.size(18.dp),
                                strokeWidth = 2.dp
                            )
                        } else {
                            Icon(
                                imageVector = AppIcons.Check,
                                contentDescription = null,
                                modifier = Modifier.size(16.dp)
                            )
                            Spacer(modifier = Modifier.width(6.dp))
                            Text(
                                text = if (isEdit) "Zapisz zmiany" else "Zapisz jako szkic",
                                fontSize = 13.sp,
                                fontWeight = FontWeight.SemiBold
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FormSectionTitle(title: String) {
    Surface(
        color = EditorColors.SurfaceSubtle,
        shape = RoundedCornerShape(8.dp),
        border = BorderStroke(1.dp, EditorColors.BorderSubtle),
        modifier = Modifier.fillMaxWidth()
    ) {
        Text(
            text = title,
            fontSize = 12.sp,
            fontWeight = FontWeight.Bold,
            color = EditorColors.TextPrimary,
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
            letterSpacing = 0.3.sp
        )
    }
}
